import type { Translations } from '@/i18n/types';

const it: Translations = {
  /* ── Common ───────────────────────────────────────────────── */
  'common.add': 'Aggiungi',
  'common.save': 'Salva',
  'common.create': 'Crea',
  'common.edit': 'Modifica',
  'common.delete': 'Elimina',
  'common.cancel': 'Annulla',
  'common.close': 'Chiudi',
  'common.back': 'Indietro',
  'common.skip': 'Salta',
  'common.search': 'Cerca',
  'common.retry': 'Riprova',
  'common.reloadPage': 'Ricarica pagina',
  'offline.title': 'Sei offline',
  'offline.message': 'Al momento non sei connesso a Internet.',
  'common.loading': 'Caricamento…',
  'common.optional': '(opzionale)',
  'common.yes': 'Sì',
  'common.no': 'No',
  'common.none': '— Nessuno —',
  'common.notSet': '— Non impostato —',
  'common.deleting': 'Eliminazione…',
  'common.applying': 'Applicazione…',
  'common.closeDialog': 'Chiudi finestra',
  'common.prev': 'Prec.',
  'common.next': 'Succ.',

  /* ── Filter UI ────────────────────────────────────────────── */
  'filter.add': 'Aggiungi filtro',
  'filter.allActive': 'Tutti i filtri attivi',
  'filter.clearAll': 'Cancella tutto',
  'filter.clear': 'Cancella',
  'filter.apply': 'Applica',
  'filter.cancel': 'Annulla',
  'filter.condition': 'Condizione',
  'filter.presets': 'Predefiniti',
  'filter.customRange': 'Intervallo personalizzato',
  'filter.from': 'Da',
  'filter.to': 'A',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Valore',
  'filter.searchOptions': 'Cerca opzioni...',
  'filter.noOptions': 'Nessuna opzione trovata',
  'filter.loadingOptions': 'Caricamento opzioni...',

  /* ── Auth ─────────────────────────────────────────────────── */
  'auth.signIn': 'Accedi',
  'auth.signInWithGoogle': 'Accedi con Google',
  'auth.signInWithApple': 'Accedi con Apple',
  'auth.or': 'oppure',
  'auth.signOut': 'Esci',
  'auth.welcome': 'Benvenuto',
  'auth.subtitle': 'Kart Editor',
  'auth.signingIn': 'Accesso in corso...',

  /* ── Settings ───────────────────────────────────────────────── */
  'settings.title': 'Impostazioni',
  'settings.account': 'Account',
  'settings.profile': 'Profilo',
  'settings.displayName': 'Nome visualizzato',
  'settings.saveName': 'Salva nome',
  'settings.nameSaved': 'Nome aggiornato correttamente',
  'settings.linkedAccounts': 'Account collegati',
  'settings.linkGoogle': 'Collega account Google',
  'settings.linkApple': 'Collega account Apple',
  'settings.unlink': 'Scollega',
  'settings.linked': 'Collegato',
  'settings.notLinked': 'Non collegato',
  'settings.cannotUnlinkOnlyProvider': 'Deve rimanere collegato almeno un metodo di accesso.',
  'settings.language': 'Lingua',
  'settings.tracks': 'Tracciati',
  'settings.categories': 'Categorie',
  'settings.drivers': 'Piloti',
  'settings.userId': 'ID utente',
  'settings.signOut': 'Esci',
  'settings.connectedApps': 'Applicazioni connesse',
  'settings.connectedAppsDesc': 'Gestisci assistenti IA, IDE e strumenti autorizzati ad accedere ai tuoi dati di telemetria tramite MCP.',
  'settings.noConnectedApps': 'Nessuna applicazione connessa. Autorizza un client MCP (come Claude Desktop o Cursor) per vederlo qui.',
  'settings.authorizedOn': 'Autorizzato il',
  'settings.lastUsed': 'Ultimo utilizzo',
  'settings.neverUsed': 'Mai',
  'settings.revoke': 'Revoca',
  'settings.revokeAppConfirm': 'Sei sicuro di voler revocare l\'accesso per questa applicazione? Tutte le sessioni attive smetteranno immediatamente di funzionare.',
  'settings.appRevokedSuccess': 'Accesso all\'applicazione revocato correttamente',
  'settings.copyKey': 'Copia snippet',
  'settings.keyCopied': 'Copiato negli appunti',
  'settings.mcpConfigSnippet': 'Configurazione client MCP',
  'settings.mcpConfigDesc': 'Aggiungi questa configurazione a Claude Desktop (claude_desktop_config.json) o alle impostazioni MCP di Cursor. L\'autenticazione verrà completata automaticamente tramite il browser:',
  'settings.deleteAccount': 'Elimina account',
  'settings.deleteAccountDesc': 'Elimina definitivamente il tuo account e tutte le sessioni, i dati di telemetria, le analisi salvate, i materiali, i setup del kart e le condivisioni associate.',
  'settings.deleteAccountConfirmTitle': 'Eliminare definitivamente l\'account?',
  'settings.deleteAccountConfirmDesc': 'Questa azione non può essere annullata. Tutte le tue sessioni, i dati di telemetria, le analisi salvate, i materiali, i setup del kart, i team e le condivisioni delle sessioni verranno eliminati immediatamente e in modo permanente.',
  'settings.deleteAccountConfirmPrompt': 'Per confermare, digita "{phrase}" nel campo qui sotto:',
  'settings.deleteAccountConfirmPhrase': 'elimina il mio account',
  'settings.deleteAccountConfirmButton': 'Elimina il mio account',

  /* ── Common fields ────────────────────────────────────────── */
  'common.brand': 'Marca',
  'common.type': 'Tipo',
  'common.nr': 'N°',
  'common.nrInfo': 'Numero progressivo per identificare questo elemento.',
  'common.year': 'Anno',
  'common.stiffness': 'Durezza',
  'common.width': 'Larghezza',
  'common.position': 'Posizione',

  /* ── Tabs ──────────────────────────────────────────────────── */
  'tab.engines': 'Motori',
  'tab.tyres': 'Pneumatici',
  'tab.chassis': 'Telai',
  'tab.carburetors': 'Carburatori',
  'tab.axles': 'Assali',
  'tab.hubs': 'Mozzi',
  'tab.spark_plugs': 'Candele',
  'tab.tracks': 'Tracciati',
  'tab.categories': 'Categorie',
  'tab.drivers': 'Piloti',
  'tab.races': 'Gare',
  'tab.users': 'Utenti',
  'tab.settings': 'Impostazioni',

  /* ── Subtabs ──────────────────────────────────────────────── */

  /* ── Navigation ────────────────────────────────────────────── */
  'nav.administration': 'Amministrazione',

  /* ── CRUD ──────────────────────────────────────────────────── */
  'crud.requiredField': '{field} è obbligatorio',
  'crud.updated': '{entity} aggiornato',
  'crud.created': '{entity} creato',
  'crud.deleted': '{entity} eliminato',
  'crud.confirmDelete': 'Eliminare {entity} "{name}"?\nQuesta azione non può essere annullata.',
  'crud.emptyState': 'Nessun elemento in {title}. Aggiungi il tuo primo {entity} qui sopra.',
  'crud.editEntity': 'Modifica {entity}',
  'crud.newEntity': 'Nuovo {entity}',

  /* ── Entities ──────────────────────────────────────────────── */
  'entity.engine': 'Motore',
  'entity.tyre': 'Pneumatico',
  'entity.chassis': 'Telaio',
  'entity.carburetor': 'Carburatore',
  'entity.axle': 'Assale',
  'entity.hub': 'Mozzo',
  'entity.spark_plug': 'Candela',
  'entity.track': 'Tracciato',
  'entity.category': 'Categoria',
  'entity.driver': 'Pilota',

  /* ── Sessions ──────────────────────────────────────────────── */

  /* ── Session Setup Dialog ──────────────────────────────────── */

  /* ── Search Help Dialog ────────────────────────────────────── */

  /* ── Import Dialog ─────────────────────────────────────────── */
  'import.title': 'Importa PDF Gare',
  'import.uploadDesc': 'Carica i PDF dei risultati di gara RMC. Verranno analizzati e mostrati in anteprima prima dell\'importazione.',
  'import.dragDropPdf': 'Trascina e rilascia qui i file PDF',
  'import.orBrowse': 'oppure',
  'import.browseFiles': 'sfoglia i file',
  'import.onlyPdfAccepted': 'Sono accettati solo file PDF.',
  'import.filesSelected': '{n} file selezionato/i',
  'import.upload': 'Carica e analizza',
  'import.uploadingPct': 'Caricamento… {n}%',
  'import.parseFailed': 'Impossibile analizzare la risposta.',
  'import.uploadFailed': 'Caricamento non riuscito. Riprova.',
  'import.connectionLost': 'Connessione persa.',
  'import.uploadAborted': 'Caricamento annullato.',
  'import.reviewDesc': 'Verifica le sessioni analizzate. Deseleziona quelle che non desideri importare.',
  'import.selectAll': 'Seleziona tutto',
  'import.deselectAll': 'Deseleziona tutto',
  'import.existsInDb': 'Esistente — verrà sovrascritto',
  'import.entriesCount': '{n} iscritti',
  'import.submit': 'Importa',
  'import.submitting': 'Importazione sessioni…',
  'import.importedCount': '{n} sessione/i importata/e.',
  'import.successMessage': 'Sessioni importate correttamente!',
  'import.noNewSessions': 'Nessuna nuova sessione importata.',
  'import.imported': 'Importate',
  'import.skipped': 'Ignorate',
  'import.errors': 'Errori',
  'import.importButton': 'Importa PDF',

  /* ── Kart Setup Dialog ─────────────────────────────────────── */

  /* ── Setup Tab ──────────────────────────────────────────────── */

  /* ── Tyre Pressures ────────────────────────────────────────── */

  /* ── Front Geometry ────────────────────────────────────────── */

  /* ── Rear Config ───────────────────────────────────────────── */

  /* ── Engine & Gear ──────────────────────────────────────────── */

  /* ── Carburetor Tuning ──────────────────────────────────────── */

  /* ── Chassis Select ─────────────────────────────────────────── */

  /* ── Notes ──────────────────────────────────────────────────── */

  /* ── Propagation Wizard ─────────────────────────────────────── */

  /* ── Propagation field labels ───────────────────────────────── */

  /* ── Tracks ─────────────────────────────────────────────────── */
  'tracks.name': 'Nome tracciato',
  'tracks.latitude': 'Latitudine',
  'tracks.longitude': 'Longitudine',
  'tracks.searchAddress': 'Cerca un indirizzo…',
  'tracks.coordinates': 'Coordinate',
  'tracks.clickToAdjust': 'Fai clic sulla mappa per regolare il segnaposto',
  'tracks.tabLocation': 'Posizione',
  'tracks.tabAliases': 'Alias',
  'tracks.sessionTracks': 'Tracciati sessioni',
  'tracks.raceEventTracks': 'Tracciati eventi gara',
  'tracks.noSessionTracks': 'Nessun tracciato di sessione disponibile',
  'tracks.noRaceEventTracks': 'Nessun tracciato di evento gara disponibile',
  'tracks.linkedAliases': '{sessions} tracciati sessioni · {races} tracciati gara',
  'tracks.satelliteImage': 'Immagine satellitare',
  'tracks.fetchImage': 'Scarica immagine satellitare',
  'tracks.refetchImage': 'Riscarica immagine satellitare',
  'tracks.fetchingImage': 'Scaricamento immagine…',
  'tracks.imageFetched': 'Immagine satellitare scaricata correttamente',
  'tracks.imageFetchFailed': 'Impossibile scaricare l\'immagine satellitare',
  'tracks.noImage': 'Nessuna immagine satellitare',
  'tracks.imageZoom': 'Livello di zoom',
  'tracks.autoZoom': 'Zoom automatico',

  /* ── Categories ─────────────────────────────────────────────── */
  'categories.name': 'Nome',
  'categories.aliases': 'Alias categoria',
  'categories.noAliases': 'Nessun nome di categoria disponibile',
  'categories.linkedAliases': '{count} alias collegati',

  /* ── Races ──────────────────────────────────────────────────── */
  'races.title': 'Sessioni di gara',
  'races.loading': 'Caricamento sessioni di gara…',
  'races.empty': 'Nessuna sessione di gara presente. Importa i PDF delle gare per iniziare.',
  'races.loadFailed': '⚠ Impossibile caricare le sessioni di gara: {error}',
  'races.date': 'Data',
  'races.track': 'Tracciato',
  'races.sessionType': 'Tipo',
  'races.category': 'Categoria',
  'races.entries': 'Iscritti',
  'races.laps': 'Giri',
  'races.championship': 'Campionato',
  'races.deleteConfirmTitle': 'Elimina sessione di gara',
  'races.deleteConfirmMessage': 'Sei sicuro di voler eliminare la sessione <strong>{type}</strong> a <strong>{track}</strong> del <strong>{date}</strong>? Tutti gli iscritti e i dati dei giri verranno rimossi definitivamente.',
  'races.deleteConfirmButton': 'Elimina sessione',
  'races.deleted': 'Sessione di gara eliminata',
  'races.detailTitle': 'Sessione di gara',
  'races.detailSubtitle': '{type} · {track} · {date}',
  'races.position': 'Pos',
  'races.kart': 'Kart',
  'races.driver': 'Pilota',
  'races.firstName': 'Nome',
  'races.lastName': 'Cognome',
  'races.nationality': 'Naz',
  'races.grid': 'Griglia',
  'races.bestLap': 'Miglior giro',
  'races.totalTime': 'Tempo totale',
  'races.penalty': 'Pen',
  'races.gap': 'Distacco',
  'races.status': 'Stato',
  'races.deleteEntry': 'Elimina iscritto',
  'races.entryDeleted': 'Iscritto eliminato',
  'races.saved': 'Sessione di gara salvata',
  'races.searchPlaceholder': 'Cerca gare… es. Castelletto o type:race',
  'races.querySyntaxHelp': 'Guida alla sintassi di ricerca',
  'races.sessionCount': '{count} sessioni',
  'races.sessionCountSingular': '1 sessione',

  /* ── Race Search Help Dialog ──────────────────────────────── */
  'raceSearchHelp.title': 'Sintassi di ricerca gare',
  'raceSearchHelp.subtitle': 'Filtra le sessioni di gara usando coppie campo:valore. Più termini sono combinati con AND.',
  'raceSearchHelp.bareText': '(testo libero)',
  'raceSearchHelp.bareTextDesc': 'Corrisponde al nome del tracciato o del campionato che contiene il testo.',
  'raceSearchHelp.track': 'track',
  'raceSearchHelp.trackDesc': 'Corrisponde al nome del tracciato che contiene il valore.',
  'raceSearchHelp.championship': 'championship',
  'raceSearchHelp.championshipDesc': 'Corrisponde al nome del campionato che contiene il valore.',
  'raceSearchHelp.category': 'category',
  'raceSearchHelp.categoryDesc': 'Corrisponde al nome della categoria che contiene il valore.',
  'raceSearchHelp.type': 'type',
  'raceSearchHelp.typeDesc': 'Corrisponde al tipo o all\'etichetta della sessione (es. race, qualifying, heat).',
  'raceSearchHelp.entries': 'entries',
  'raceSearchHelp.entriesDesc': 'Filtra per numero di iscritti. Supporta =, >, >=, <, <= e intervalli.',
  'raceSearchHelp.laps': 'laps',
  'raceSearchHelp.lapsDesc': 'Filtra per numero totale di giri. Supporta operatori di confronto e intervalli.',
  'raceSearchHelp.season': 'season',
  'raceSearchHelp.seasonDesc': 'Filtra per anno della stagione di campionato. Supporta operatori di confronto.',
  'raceSearchHelp.from': 'from',
  'raceSearchHelp.fromDesc': 'Gare a partire da una data. Accetta YYYY-MM-DD o date relative: -7d, -2w, -3m, -1y.',
  'raceSearchHelp.to': 'to',
  'raceSearchHelp.toDesc': 'Gare fino a una data. Stessi formati di "from".',
  'raceSearchHelp.operatorsTitle': 'Operatori e logica',
  'raceSearchHelp.numericComparisons': 'Confronti numerici:',
  'raceSearchHelp.range': 'Intervallo:',
  'raceSearchHelp.rangeDesc': '— corrisponde a valori compresi tra 5 e 20 inclusi.',
  'raceSearchHelp.booleanLogic': 'Logica booleana:',
  'raceSearchHelp.booleanLogicDesc': 'I termini sono uniti implicitamente con',
  'raceSearchHelp.quotes': 'Virgolette:',
  'raceSearchHelp.quotesDesc': 'Racchiudi i valori con spazi tra virgolette doppie, es.',
  'raceSearchHelp.examplesTitle': 'Query di esempio',
  'raceSearchHelp.example1Desc': 'Sessioni di gara a Castelletto',
  'raceSearchHelp.example2Desc': 'Gare del campionato RMC dell\'ultimo anno',
  'raceSearchHelp.example3Desc': 'Gare con più di 20 iscritti e almeno 5 giri',

  /* ── Pagination ─────────────────────────────────────────────── */
  'pagination.eventsFound': '{n} evento/i trovato/i',

  /* ── Tyre IDs ──────────────────────────────────────────────── */

  /* ── Settings ──────────────────────────────────────────────── */
  'settings.users': 'Utenti',

  /* ── Drivers ──────────────────────────────────────────────── */
  'drivers.canonicalDriver': 'Pilota principale',
  'drivers.aliases': 'Alias',
  'drivers.aliasesHeader': 'Alias',
  'drivers.raceDrivers': 'Piloti gara',
  'drivers.sessionDrivers': 'Piloti sessione',
  'drivers.noRaceDrivers': 'Nessun pilota di gara disponibile',
  'drivers.noSessionDrivers': 'Nessun pilota di sessione disponibile',
  'drivers.selectDriver': 'Seleziona un pilota…',
  'drivers.searchDrivers': 'Cerca piloti…',
  'drivers.createTitle': 'Nuovo alias pilota',
  'drivers.editTitle': 'Modifica alias pilota',
  'drivers.stepCanonical': 'Seleziona pilota',
  'drivers.stepAliases': 'Seleziona alias',

  /* ── Sharing ─────────────────────────────────────────────── */
  
  /* ── Users ──────────────────────────────────────────────── */
  'users.title': 'Utenti',
  'users.empty': 'Nessun utente trovato',
  'users.loading': 'Caricamento utenti...',
  'users.searchPlaceholder': 'Cerca utenti...',
  'users.name': 'Nome',
  'users.email': 'Email',
  'users.user': 'Utente',
  'users.admin': 'Admin',
  'users.owner': 'Proprietario',
  'users.promote': 'Promuovi',
  'users.demote': 'Revoca admin',
  'users.promoted': '{name} promosso ad amministratore',
  'users.demoted': 'Privilegi di amministratore rimossi per {name}',
  'users.cannotDemoteOwner': 'Il proprietario non può essere declassato',
  'users.cannotDemoteSelf': 'Non puoi rimuovere i tuoi stessi privilegi di amministratore',
  'users.confirmPromote': 'Promuovere {name} ad amministratore? Otterrà accesso completo all\'editor.',
  'users.confirmDemote': 'Rimuovere l\'accesso da amministratore a {name}? Non potrà più utilizzare l\'editor.',
  'users.accessDenied': 'Accesso negato',
  'users.accessDeniedMessage': 'Non disponi dei privilegi di amministratore. Contatta un amministratore per richiedere l\'accesso.',
  'users.joinedAt': 'Iscritto il',
  'users.lastLogin': 'Ultimo accesso',
  'users.count': '{count} utenti',
  'users.countSingular': '1 utente',

  /* ── User Search Help Dialog ────────────────────────────── */
  'userSearchHelp.title': 'Ricerca utenti',
  'userSearchHelp.subtitle': 'Filtra l\'elenco degli utenti usando query specifiche per campo o testo libero.',
  'userSearchHelp.bareText': 'Testo libero',
  'userSearchHelp.bareTextDesc': 'Corrisponde sia al nome che all\'indirizzo email.',
  'userSearchHelp.name': 'name:',
  'userSearchHelp.nameDesc': 'Filtra per nome visualizzato dell\'utente.',
  'userSearchHelp.email': 'email:',
  'userSearchHelp.emailDesc': 'Filtra per indirizzo email.',
  'userSearchHelp.login': 'login:',
  'userSearchHelp.loginDesc': 'Filtra per data dell\'ultimo accesso. Supporta valori singoli (-7d) o intervalli (-30d..today). Usa date relative (-7d, -2w, -3m), assolute (2026-01-15) o today.',
  'userSearchHelp.created': 'created:',
  'userSearchHelp.createdDesc': 'Filtra per data di creazione dell\'account. Supporta valori singoli (-30d) o intervalli (-1y..today). Usa date relative (-7d, -2w, -3m), assolute (2026-01-15) o today.',
  'userSearchHelp.examplesTitle': 'Esempi',
  'userSearchHelp.example1Desc': 'Trova utenti di nome Yanick',
  'userSearchHelp.example2Desc': 'Trova utenti con indirizzo Gmail',
  'userSearchHelp.example3Desc': 'Trova utenti che hanno effettuato l\'accesso negli ultimi 7 giorni',
  'userSearchHelp.example4Desc': 'Trova utenti creati in un intervallo di date',

  /* ── Bugs tab ───────────────────────────────────────────────── */
  'tab.bugs': 'Bug',
  'bugs.title': 'Segnalazioni di bug',
  'bugs.empty': 'Nessuna segnalazione di bug trovata.',
  'bugs.loading': 'Caricamento segnalazioni di bug…',
  'bugs.searchPlaceholder': 'Cerca segnalazioni di bug…',
  'bugs.reporter': 'Segnalato da',
  'bugs.description': 'Descrizione',
  'bugs.pageUrl': 'URL pagina',
  'bugs.status': 'Stato',
  'bugs.statusOpen': 'Aperto',
  'bugs.statusResolved': 'Risolto',
  'bugs.createdAt': 'Creato il',
  'bugs.resolvedAt': 'Risolto il',
  'bugs.resolve': 'Risolvi',
  'bugs.reopen': 'Riapri',
  'bugs.resolved': 'Segnalazione di bug risolta.',
  'bugs.reopened': 'Segnalazione di bug riaperta.',
  'bugs.count': '{count} segnalazioni',
  'bugs.countSingular': '1 segnalazione',
  'bugs.detailTitle': 'Segnalazione di bug',
  'bugs.userAgent': 'User Agent',
  'bugs.screenSize': 'Dimensioni schermo',
  'bugs.appLanguage': 'Lingua',
  'bugs.copy': 'Copia testo',
  'bugs.copied': 'Copiato negli appunti',

  /* ── Bug search help ────────────────────────────────────────── */
  'bugSearchHelp.title': 'Sintassi di ricerca',
  'bugSearchHelp.subtitle': 'Usa questi prefissi per affinare la ricerca.',
  'bugSearchHelp.bareText': 'testo libero',
  'bugSearchHelp.bareTextDesc': 'Cerca nella descrizione e nel nome del segnalatore.',
  'bugSearchHelp.status': 'status:open',
  'bugSearchHelp.statusDesc': 'Filtra per stato (open o resolved).',
  'bugSearchHelp.email': 'email:valore',
  'bugSearchHelp.emailDesc': 'Filtra per email del segnalatore.',
  'bugSearchHelp.examplesTitle': 'Esempi',
  'bugSearchHelp.example1Desc': 'Trova tutte le segnalazioni di bug aperte.',
  'bugSearchHelp.example2Desc': 'Trova segnalazioni di bug inviate da utenti Gmail.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'Si è verificato un errore che è stato segnalato automaticamente.',
  'errorBoundary.title': 'Qualcosa è andato storto',
  'errorBoundary.message': 'Si è verificato un errore imprevisto che è stato segnalato al server.',
  'errorBoundary.reload': 'Ricarica pagina',
  'errorBoundary.reset': 'Riprova',
  'errorBoundary.showDetails': 'Mostra dettagli errore',
  'errorBoundary.hideDetails': 'Nascondi dettagli errore',

  /* ── Profiles ───────────────────────────────────────────────── */
  'tab.profiles': 'Profili',
  'profiles.engineProfiles': 'Profili motore',
  'profiles.tyreProfiles': 'Profili pneumatici',
  'profiles.addEngineProfile': 'Aggiungi profilo motore',
  'profiles.editEngineProfile': 'Modifica profilo motore',
  'profiles.addTyreProfile': 'Aggiungi profilo pneumatico',
  'profiles.editTyreProfile': 'Modifica profilo pneumatico',
  'profiles.brand': 'Marca',
  'profiles.type': 'Tipo',
  'profiles.compound': 'Mescola',
  'profiles.compoundDry': 'Asciutto (Slick)',
  'profiles.compoundWet': 'Bagnato (Rain)',
  'profiles.deleteBlocked': 'Impossibile eliminare — ancora utilizzato da materiale in inventario.',
  'profiles.emptyEngineProfiles': 'Nessun profilo motore presente.',
  'profiles.emptyTyreProfiles': 'Nessun profilo pneumatico presente.',
  'entity.engineProfile': 'Profilo motore',
  'entity.tyreProfile': 'Profilo pneumatico',

  /* ── Track Shape Studio ────────────────────────────────────── */
  'trackStudio.title': 'Studio Geometria e Annotazioni Tracciato',
  'trackStudio.subtitle': 'Progetta la geometria del circuito, modifica le spline e posiziona le annotazioni di curve e settori',
  'trackStudio.openStudio': 'Geometria e annotazioni tracciato',
  'trackStudio.modeSpline': 'Geometria e spline',
  'trackStudio.modeCorners': 'Curve',
  'trackStudio.modeSectors': 'Settori',
  'trackStudio.modeSegments': 'Segmenti',
  'trackStudio.bakeSegments': 'Genera 200 segmenti',
  'trackStudio.rebakeSegments': 'Rigenera 200 segmenti',
  'trackStudio.segmentsBakedSuccess': '{count} segmenti del tracciato generati correttamente',
  'trackStudio.segmentsCount': '{count} segmenti',
  'trackStudio.segmentsStatusBaked': '200 segmenti generati',
  'trackStudio.segmentsStatusNotBaked': 'Non ancora generati',
  'trackStudio.segmentsHint': 'Suddivide la linea centrale del tracciato in 200 segmenti GPS statici equidistanti a partire dalla linea di Partenza / Arrivo per un confronto dei giri ad alta precisione.',
  'trackStudio.segmentDetails': 'Dettagli segmento',
  'trackStudio.segmentIndex': 'Segmento #{index}',
  'trackStudio.avgSegmentLength': 'Lunghezza media segmento',
  'trackStudio.searchSegments': 'Cerca segmenti...',
  'trackStudio.modeAnnotations': 'Annotazioni curve e settori',
  'trackStudio.cornersCount': 'Curve del tracciato ({count})',
  'trackStudio.sectorsCount': 'Settori cronometrati ({count})',
  'trackStudio.clickCornersHint': 'Fai clic in qualsiasi punto del nastro d\'asfalto per aggiungere una curva. Trascina il punto di corda per spostarla o regola i punti di ingresso e uscita.',
  'trackStudio.clickSectorsHint': 'Fai clic in qualsiasi punto del nastro d\'asfalto per posizionare un indicatore di intertempo. Trascina gli indicatori per riposizionarli lungo il circuito.',
  'trackStudio.trackLength': 'Lunghezza tracciato: {length}m',
  'trackStudio.trackWidth': 'Larghezza tracciato',
  'trackStudio.defaultTrackWidth': 'Larghezza predefinita tracciato',
  'trackStudio.pointWidth': 'Larghezza punto',
  'trackStudio.customWidth': 'Larghezza personalizzata per questo punto',
  'trackStudio.useDefaultWidth': 'Usa predefinita ({width}m)',
  'trackStudio.resetPointWidth': 'Ripristina predefinita',
  'trackStudio.segmentWidth': 'Strumento larghezza segmento',
  'trackStudio.segmentWidthDesc': 'Regola la larghezza o crea transizioni graduali di larghezza tra i punti',
  'trackStudio.fromPoint': 'Dal punto',
  'trackStudio.toPoint': 'Al punto',
  'trackStudio.startWidth': 'Larghezza iniziale',
  'trackStudio.endWidth': 'Larghezza finale',
  'trackStudio.transitionMode': 'Modalità di transizione',
  'trackStudio.modeConstant': 'Larghezza costante',
  'trackStudio.modeGradient': 'Transizione graduale (Gradiente)',
  'trackStudio.applySegment': 'Applica larghezza al segmento',
  'trackStudio.resetSegment': 'Ripristina larghezza segmento',
  'trackStudio.segmentApplied': 'Larghezza aggiornata per i punti da {from} a {to}',
  'trackStudio.controlPoints': 'Punti di controllo linea centrale ({count})',
  'trackStudio.addPointHint': 'Fai clic sulla curva o sull\'area di disegno per inserire punti di controllo. Trascina i punti per modificare la forma.',
  'trackStudio.selectedPoint': 'Punto selezionato #{index}',
  'trackStudio.deletePoint': 'Elimina punto',
  'trackStudio.evenSpacePoints': 'Distribuisci punti uniformemente',
  'trackStudio.reverseDirection': 'Inverti direzione tracciato',
  'trackStudio.layerSatellite': 'Immagine satellitare',
  'trackStudio.layerCenterline': 'Spline linea centrale',
  'trackStudio.layerBoundaries': 'Limiti della pista',
  'trackStudio.layerAnnotations': 'Annotazioni e indicatori',
  'trackStudio.undo': 'Annulla (Cmd+Z)',
  'trackStudio.redo': 'Ripeti (Cmd+Shift+Z)',
  'trackStudio.reset': 'Ripristina modifiche',
  'trackStudio.saveSuccess': 'Geometria e annotazioni del tracciato salvate correttamente!',
  'trackStudio.autoSaveSuccess': 'Geometria e annotazioni salvate automaticamente',
  'trackStudio.saveError': 'Impossibile salvare la geometria del tracciato: {error}',
  'trackStudio.noCenterline': 'Fai clic sull\'immagine satellitare per creare il primo punto di controllo della linea centrale del tracciato.',
  'trackStudio.annotations': 'Annotazioni tracciato ({count})',
  'trackStudio.addCorner': 'Aggiungi curva (T{next})',
  'trackStudio.addSector': 'Aggiungi indicatore settore',
  'trackStudio.addFinishLine': 'Imposta linea di Partenza / Arrivo',
  'trackStudio.cornerName': 'Nome curva',
  'trackStudio.cornerDirection': 'Direzione curva',
  'trackStudio.directionLeft': 'Curva a sinistra',
  'trackStudio.directionRight': 'Curva a destra',
  'trackStudio.entry': 'Ingresso (Inserimento / Frenata)',
  'trackStudio.apex': 'Punto di corda (Apex)',
  'trackStudio.exit': 'Uscita (Allargamento / Pieno gas)',
  'trackStudio.deleteAnnotation': 'Elimina annotazione',
  'trackStudio.noAnnotations': 'Nessuna annotazione creata. Fai clic sulla forma del tracciato per posizionare curve, settori o la linea di partenza/arrivo.',
  'trackStudio.finishLinePlaced': 'La linea del traguardo è attiva al {pos}% del giro.',
  'trackStudio.closeConfirm': 'Ci sono modifiche non salvate. Vuoi davvero chiudere lo Studio?',
  'trackStudio.menuAddCorner': 'Aggiungi curva',
  'trackStudio.menuAddSector': 'Aggiungi indicatore settore',
  'trackStudio.menuSetFinishLine': 'Imposta linea di Partenza / Arrivo',
  'trackStudio.menuTrackPosition': 'Posizione sul tracciato',
  'trackStudio.clickTrackHint': 'Fai clic in qualsiasi punto del nastro d\'asfalto per aggiungere una curva, un settore o impostare la linea di Partenza/Arrivo.',
  'trackStudio.drivingDirection': 'Senso di marcia',
  'trackStudio.reverseDrivingDirection': 'Inverti senso di marcia',
  'trackStudio.drivingDirectionDesc': 'Inverti il senso di percorrenza del circuito. Tutte le sequenze delle curve e i settori cronometrati si adatteranno automaticamente.',
  'trackStudio.multiplePointsSelected': '{count} punti selezionati',
  'trackStudio.selectAll': 'Seleziona tutto (Cmd+A)',
  'trackStudio.deselectAll': 'Deseleziona (Esc)',
  'trackStudio.deleteSelectedPoints': 'Elimina {count} punti',
  'trackStudio.customWidthSelection': 'Larghezza personalizzata (tutti i selezionati)',
  'tracks.reanalyzeLaps': 'Rianalizza giri del tracciato',
  'tracks.reanalyzeAllLaps': 'Rianalizza tutti i giri',
  'tracks.reanalyzeQueued': '{enqueued} giro/i messo/i in coda per l\'analisi ({skipped} già aggiornato/i).',
  'tracks.reanalyzeForceQueued': '{enqueued} giro/i messo/i in coda per il ricalcolo completo.',
  'tracks.reanalyzeAllUpToDate': 'Tutti i {skipped} giri sono già aggiornati con i dati attuali del tracciato.',
  'tracks.reanalyzeConfirmTitle': 'Rianalizza giri del tracciato',
  'tracks.reanalyzeConfirmMessage': 'L\'hash dei dati del tracciato per "{name}" non è cambiato ({count} giro/i già aggiornato/i). Vuoi forzare comunque un ricalcolo completo per questo tracciato?',
  'tracks.reanalyzeConfirmButton': 'Rianalizza giri',
  'tracks.reanalyzeAllConfirmTitle': 'Rianalizza tutti i giri',
  'tracks.reanalyzeAllConfirmMessage': 'Sei sicuro di voler avviare un ricalcolo completo di tutti i giri e di tutte le sessioni su tutti i tracciati?',

  /* ── Monitoring ────────────────────────────────────────────── */
  'tab.monitoring': 'Monitoraggio',
  'monitoring.title': 'Monitoraggio code',
  'monitoring.subtitle': 'Statistiche in tempo reale e controlli di elaborazione delle code di analisi giri e sessioni',
  'monitoring.refresh': 'Aggiorna',
  'monitoring.autoRefreshActive': 'Aggiornamento automatico ogni 3s mentre attivo',
  'monitoring.lapQueueTitle': 'Coda analisi giri',
  'monitoring.sessionQueueTitle': 'Coda analisi sessioni',
  'monitoring.pending': 'In attesa',
  'monitoring.processing': 'In elaborazione',
  'monitoring.completed': 'Completati',
  'monitoring.failed': 'Falliti',
  'monitoring.totalEligible': 'Totale idonei',
  'monitoring.actionsTitle': 'Controlli globali coda',
  'monitoring.syncUnanalyzed': 'Sincronizza non analizzati e obsoleti',
  'monitoring.syncUnanalyzedDesc': 'Scansiona tutti i tracciati e metti in coda i giri non analizzati o con geometria tracciato obsoleta',
  'monitoring.syncUnanalyzedDone': '{enqueued} giro/i messo/i in coda ({skipped} già aggiornato/i).',
  'monitoring.retryFailed': 'Riprova job falliti',
  'monitoring.retryFailedDesc': 'Riporta in attesa tutti i job di analisi giri e sessioni falliti',
  'monitoring.retryFailedDone': '{retried} job fallito/i riportato/i in attesa.',
  'monitoring.reanalyzeAll': 'Forza rianalisi di tutti i giri',
  'monitoring.reanalyzeAllDesc': 'Forza un ricalcolo completo di tutti i giri e di tutte le sessioni su tutti i tracciati',
  'monitoring.userQueuesTitle': 'Avanzamento coda per utente (Round-Robin)',
  'monitoring.userQueuesSubtitle': 'Il worker elabora i job in ordine round-robin tra gli utenti in modo che i batch grandi non blocchino gli altri utenti',
  'monitoring.userQueuesEmpty': 'Nessun job attivo o in coda per utente.',
  'monitoring.user': 'Utente',
  'monitoring.systemQueue': 'Sistema / Globale',
  'monitoring.lapsStatus': 'Job giri',
  'monitoring.sessionsStatus': 'Job sessioni',
  'monitoring.progress': 'Avanzamento',
  'monitoring.tracksTitle': 'Copertura analisi tracciati e controlli di ricalcolo',
  'monitoring.tracksSubtitle': 'Ispeziona la copertura di analisi dei giri per ciascun tracciato principale e avvia un ricalcolo incrementale o forzato',
  'monitoring.tracksEmpty': 'Nessun tracciato principale configurato.',
  'monitoring.trackCoverage': 'Copertura',
  'monitoring.upToDateLaps': 'Aggiornati',
  'monitoring.outdatedLaps': 'Obsoleti',
  'monitoring.unanalyzedLaps': 'Non analizzati',
  'monitoring.shapeReady': '200 segmenti pronti',
  'monitoring.noShape': 'Segmenti non generati',
  'monitoring.reanalyzeTrack': 'Sincronizza / Rianalizza',
  'monitoring.forceReanalyzeTrack': 'Forza tutto',

  /* ── Terms & Conditions ────────────────────────────────────── */
  'terms.gateTitle': 'Termini e Condizioni',
  'terms.gateSubtitle': 'Esamina e accetta gli ultimi Termini e Condizioni per continuare.',
  'terms.updatedBannerTitle': 'Termini e Condizioni aggiornati:',
  'terms.updatedBannerDesc': 'I nostri Termini e Condizioni sono stati aggiornati. Esamina e accetta la nuova versione per continuare.',
  'terms.checkboxLabel': 'Ho letto e accetto i Termini e Condizioni',
  'terms.acceptButton': 'Accetta e continua',
  'terms.acceptingButton': 'Accettazione…',
  'terms.loadError': 'Impossibile caricare i Termini e Condizioni. Controlla la connessione e riprova.',
  'terms.acceptError': 'Impossibile registrare la tua accettazione. Riprova.',

  /* ── Search & Filter Bar ───────────────────────────────────── */
  'search.placeholder': 'Cerca o aggiungi filtro...',
  'search.button': 'Cerca',
  'search.syntaxHelp': 'Guida alla sintassi di ricerca',
  'search.title': 'Guida alla sintassi di ricerca',
  'search.subtitle': 'Filtra e cerca utilizzando campi strutturati e operatori booleani.',
  'search.closeDialog': 'Chiudi',
  'search.field.bareTextDesc': 'Cerca nei campi di testo predefiniti',
  'search.operatorsTitle': 'Operatori',
  'search.numericComparisons': 'Confronti numerici / per data',
  'search.range': 'Intervallo (incluso)',
  'search.rangeDesc': 'Corrisponde ai valori compresi tra min e max',
  'search.booleanLogic': 'Logica booleana',
  'search.booleanLogicDesc': 'Combina più condizioni',
  'search.booleanLogicUse': 'Combina i termini con',
  'search.booleanLogicOr': 'oppure',
  'search.booleanLogicGroup': 'e raggruppa le espressioni con le parentesi',
  'search.quotes': 'Stringhe tra virgolette',
  'search.quotesDesc': 'Racchiudi i valori con più parole tra virgolette (es. track:"South Garda")',
  'search.examplesTitle': 'Esempi',

  'filter.operator.eq': 'è',
  'filter.operator.neq': 'non è',
  'filter.operator.gt': 'maggiore di',
  'filter.operator.gte': 'almeno',
  'filter.operator.lt': 'minore di',
  'filter.operator.lte': 'al massimo',
  'filter.operator.range': 'tra',
  'filter.operator.contains': 'contiene',
  'filter.remove': 'Rimuovi filtro',
  'filter.done': 'Fatto',
  'filter.clearInput': 'Cancella testo',
  'filter.selectField': 'Filtra per campo',
  'filter.pressEnter': 'Premi Invio per applicare',
  'filter.selectValues': 'Seleziona valori',
  'filter.noMatchingOptions': 'Nessuna opzione corrispondente',

  'filter.bugs.bareSearchExample': 'Cerca riepilogo, descrizione o segnalatore',
  'filter.bugs.searchSummary': 'Filtra per riepilogo del bug',
  'filter.bugs.searchDescription': 'Filtra per descrizione del bug',
  'filter.bugs.searchReporter': 'Filtra per nome o email del segnalatore',
  'filter.bugs.filterStatus': 'Filtra per stato del bug',
  'filter.bugs.filterSeverity': 'Filtra per livello di gravità',
  'filter.bugs.filterPlatform': 'Filtra per piattaforma (web, ios, android)',
  'filter.bugs.filterDate': 'Filtra per data di segnalazione',
  'filter.bugs.exampleOpenHigh': 'Bug aperti ad alta gravità',
  'filter.bugs.exampleMobileLogin': 'Problemi di accesso mobile',

  'filter.users.bareSearchExample': 'Cerca nome, email o ID utente',
  'filter.users.searchName': 'Filtra per nome visualizzato',
  'filter.users.searchEmail': 'Filtra per indirizzo email',
  'filter.users.searchId': 'Filtra per ID utente',
  'filter.users.filterRole': 'Filtra per ruolo assegnato',
  'filter.users.exampleAdmin': 'Utenti con ruolo amministratore',
  'filter.users.exampleMultiRole': 'Amministratori o editor per nome',

  'filter.races.bareSearchExample': 'Cerca stagione, tracciato, evento o pilota',
  'filter.races.searchSeason': 'Filtra per nome stagione di campionato',
  'filter.races.filterYear': 'Filtra per anno di campionato',
  'filter.races.searchCountry': 'Filtra per codice paese',
  'filter.races.searchCategory': 'Filtra per categoria',
  'filter.races.searchTrack': 'Filtra per nome tracciato',
  'filter.races.searchEvent': 'Filtra per titolo evento',
  'filter.races.filterRound': 'Filtra per numero di round',
  'filter.races.searchSession': 'Filtra per nome sessione',
  'filter.races.filterType': 'Filtra per tipo di sessione',
  'filter.races.searchDriver': 'Filtra per nome pilota',
  'filter.races.typePractice': 'Prove libere',
  'filter.races.typeQualifying': 'Qualifiche',
  'filter.races.typeHeat': 'Manche eliminatoria',
  'filter.races.typePrefinal': 'Prefinale',
  'filter.races.typeFinal': 'Finale',
  'filter.races.exampleRokTrack': 'Eventi ROK Cup a South Garda',
  'filter.races.exampleFinalDriver': 'Sessioni finali con un pilota',

  'races.season': 'Stagione',
  'races.year': 'Anno',
  'races.country': 'Paese',
  'races.event': 'Evento',
  'races.roundLabel': 'Round',
  'races.session': 'Sessione',
  'races.type': 'Tipo',
  'races.label': 'Etichetta',
  'races.time': 'Orario',

  'trackStudio.zoomIn': 'Ingrandisci',
  'trackStudio.zoomOut': 'Riduci',
  'trackStudio.resetView': 'Reimposta vista',
  'trackStudio.geoLocation': 'Posizione geografica',
  'trackStudio.finishLineTitle': 'Linea di Partenza / Arrivo',
  'trackStudio.finishLineHint': 'Trascina l’indicatore della bandiera lungo il tracciato, fai clic sul tracciato o usa il cursore in basso.',
  'trackStudio.staticGpsPosition': 'Posizione GPS statica',
  'trackStudio.distance': 'Distanza',
  'trackStudio.previous': 'Precedente',
  'trackStudio.next': 'Successivo',

  'users.terms': 'Termini',
  'users.termsAccepted': 'Accettati',
  'users.termsOutdated': 'Obsoleti',
  'users.termsPending': 'In attesa',
  'users.resetTerms': 'Reimposta termini e condizioni',
  'users.confirmResetTerms': 'Reimpostare l’accettazione dei termini e condizioni per {name}? Dovrà esaminare e accettare nuovamente i termini e condizioni prima di continuare.',
  'users.termsReset': 'Accettazione dei termini e condizioni reimpostata per {name}',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Filtra per {label}',
  'filter.removeFilter': 'Rimuovi filtro {label}',
  'filter.prevMonth': 'Mese precedente',
  'filter.nextMonth': 'Mese successivo',
  'filter.selectStartDate': 'Seleziona la data di inizio nel calendario',
  'filter.op.exact': 'Esatto',
  'filter.op.atLeast': 'Almeno',
  'filter.op.atMost': 'Al massimo',
  'filter.op.between': 'Tra',
  'filter.op.greater': 'Maggiore',
  'filter.op.less': 'Minore',
  'filter.preset.today': 'Oggi',
  'filter.preset.last7d': 'Ultimi 7 giorni',
  'filter.preset.last30d': 'Ultimi 30 giorni',
  'filter.preset.last90d': 'Ultimi 90 giorni',
  'filter.preset.last1y': 'Ultimo anno',
  'filter.preset.last7dShort': 'Ultimi 7g',
  'filter.preset.last30dShort': 'Ultimi 30g',
  'filter.preset.last90dShort': 'Ultimi 90g',
  'filter.preset.last1yShort': 'Ultimo 1a',
  'filter.sessionType.race': 'Gara',
  'filter.sessionType.final': 'Finale',
  'filter.sessionType.pre_final': 'Prefinale',
  'filter.sessionType.heat': 'Manche',
  'filter.sessionType.qualifying': 'Qualifiche',
  'filter.unit.laps': 'giri',
  'filter.field.date': 'Data',
  'filter.field.track': 'Pista',
  'filter.desc.track': 'Corrisponde al nome della pista',
  'filter.field.championship': 'Campionato',
  'filter.desc.championship': 'Nome del campionato',
  'filter.field.category': 'Categoria',
  'filter.desc.category': 'Categoria di gara canonica',
  'filter.field.sessionType': 'Tipo di sessione',
  'filter.desc.sessionType': 'Classificazione sessione (gara, manche, qualifiche, finale)',
  'filter.field.laps': 'Giri',
  'filter.field.season': 'Stagione',
  'filter.desc.season': 'Corrisponde all’anno della stagione',
  'filter.field.driver': 'Pilota',
  'filter.placeholder.driver': 'es. Yanick',
  'filter.placeholder.email': 'es. gmail.com',
  'filter.desc.email': 'Corrisponde all’indirizzo email',
  'bugs.severity': 'Gravità',
  'bugs.severityCritical': 'Critica',
  'bugs.severityHigh': 'Alta',
  'bugs.severityMedium': 'Media',
  'bugs.severityLow': 'Bassa',
  'bugs.statusInProgress': 'In corso',
  'bugs.statusClosed': 'Chiuso',
  'bugs.submitter': 'Email segnalatore',
  'filter.bugs.title': 'Sintassi di ricerca segnalazioni bug',
  'filter.bugs.subtitle': 'Filtra i bug segnalati per stato, gravità, email o parole chiave.',
  'filter.field.bugTitle': 'Riepilogo / Titolo',
  'filter.placeholder.bugTitle': 'es. crash, caricamento',
  'filter.desc.bugTitle': 'Corrisponde al titolo o alla descrizione del problema',
  'filter.desc.bugStatus': 'Stato della segnalazione (aperto, in corso, chiuso)',
  'filter.desc.bugSeverity': 'Livello di gravità del bug',
  'filter.placeholder.bugEmail': 'es. utente@esempio.it',
  'filter.desc.bugEmail': 'Corrisponde all’email del segnalatore',
  'filter.field.bugEnv': 'Ambiente',
  'filter.desc.bugEnv': 'Filtra per ambiente di segnalazione (prod, dev, local)',
  'filter.bugs.example1': 'Tutte le segnalazioni di bug aperte',
  'filter.bugs.example2': 'Problemi aperti in produzione',
  'filter.bugs.example3': 'Segnalazioni di crash aperte',
  'filter.field.driverEntries': 'Piloti iscritti',
  'filter.desc.driverEntries': 'Numero totale di piloti partecipanti alla gara',
  'filter.preset.entriesSmall': 'Piccolo (< 10)',
  'filter.preset.entriesMedium': 'Medio (10..25)',
  'filter.preset.entriesLarge': 'Grande (> 25)',
  'filter.desc.raceLaps': 'Numero di giri di gara completati',
  'filter.desc.raceDriver': 'Corrisponde al nome del pilota nei risultati di gara',
  'filter.desc.raceDate': 'Filtra le gare per periodo o intervallo di date del calendario',
  'filter.races.example1': 'Gare 2026 a Wohlen',
  'filter.races.example2': 'Griglie numerose in Senior MAX',
  'filter.races.example3': 'Gare con Yanick nell’ultimo anno',
  'filter.users.title': 'Sintassi di ricerca utenti',
  'filter.users.subtitle': 'Filtra gli utenti registrati per nome, email, ruolo o provider di autenticazione.',
  'users.colName': 'Nome',
  'users.colEmail': 'Email',
  'users.colRole': 'Ruolo',
  'users.colProvider': 'Provider di accesso',
  'users.roleAdmin': 'Admin',
  'users.roleOwner': 'Proprietario',
  'users.roleUser': 'Utente',
  'filter.desc.userName': 'Corrisponde al nome visualizzato dell’utente',
  'filter.desc.userRole': 'Ruolo utente (admin, proprietario, utente)',
  'filter.desc.authProvider': 'Provider di autenticazione',
  'filter.provider.password': 'Password',
  'filter.desc.userDate': 'Data di creazione o di ultima attività',
  'filter.users.example1': 'Trova gli utenti corrispondenti a Yanick',
  'filter.users.example2': 'Amministratori della piattaforma',
  'filter.users.example3': 'Accessi Google negli ultimi 30 giorni',
};

export default it;

